import {ClientReportColumn, ClientReportInfo, ClientReportSortBy, SortOrder} from "./domain";
import {ClientReportRowItem} from "./ClientReportListRowItem";
import {ClientReportListAction} from "./clientReportReducer";
import React from 'react';
import {LoadingSpinner, Paths} from "../../common";
import {CaretDownIcon, CaretUpIcon, Icon} from "evergreen-ui";

export const ClientReportColumnsFull: Readonly<ClientReportColumn[]> = [
    {name: 'Report Name', attr: "clientReportFiles", format: "string[]"},
    {name: 'Report Type', attr: 'reportType', format: "string"},
    {name: 'Report Reference', attr: 'sfOperationCaseNumber', format: "string"},
    {name: 'Authorization', attr: 'authorization.bhpnAuthNumber', format: "string"},
    {name: 'Submitted Date', attr: 'submittedAt', format: "date"},
    {name: 'Submitted By', attr: 'submittedBy', format: "string"},
    {name: 'Report Status', attr: 'reportStatus', format: "string"},
    {name: 'Authorized/Closed Date', attr: 'authorizedDate', format: "date"},
]

export const ClientReportColumnsLite: Readonly<ClientReportColumn[]> = [
    {name: 'Report Name', attr: "clientReportFiles", format: "string[]"},
    {name: 'Report Type', attr: 'reportType', format: "string"},
    {name: 'Report Reference', attr: 'sfOperationCaseNumber', format: "string"},
    {name: 'Submitted Date', attr: 'submittedAt', format: "date"},
    {name: 'Submitted By', attr: 'submittedBy', format: "date"},
    {name: 'Report Status', attr: 'reportStatus', format: "string"},
]

type ClientProfileReportTableFullProps = {
    reports: Array<ClientReportInfo>,
    selected: ClientReportInfo | null;
    sortBy: ClientReportSortBy;
    loading: boolean;
    dispatch: React.Dispatch<ClientReportListAction>;
    isApplyGroupBySfOperationCase?: boolean;
}

export function ClientProfileReportTableFull({
                                                 reports,
                                                 selected,
                                                 dispatch,
                                                 sortBy,
                                                 loading,
                                                 isApplyGroupBySfOperationCase = true,
                                             }: ClientProfileReportTableFullProps) {
    const setSortByColumn = (newSortColumn: Paths<ClientReportInfo>) => {
        if (newSortColumn === 'clientReportFileNames' || newSortColumn === 'authorizedDate') {
            return;
        }

        let newSortOrder = SortOrder.DESC;
        if (newSortColumn === sortBy.column && sortBy.order === newSortOrder) {
            newSortOrder = SortOrder.ASC;
        }

        dispatch({type: 'SET_SORT_BY', sortBy: {column: newSortColumn, order: newSortOrder}});
    }

    return <ClientProfileReportTable reports={reports}
                                     selected={selected}
                                     hideModalFunc={() => dispatch({type: 'HIDE_VIEW_MODAL'})}
                                     setSelectedFunc={(report) => dispatch({type: 'SET_SELECTED', selected: report})}
                                     setSortBy={setSortByColumn}
                                     sortBy={sortBy}
                                     isLite={false}
                                     loading={loading}
                                     columns={ClientReportColumnsFull as ClientReportColumn[]}
                                     isApplyGroupBySfOperationCase={isApplyGroupBySfOperationCase}/>
}

type ClientProfileReportTableLiteProps = {
    reports: Array<ClientReportInfo>,
    selected: ClientReportInfo | null;
    hideModalFunc: () => void;
    loading: boolean;
    setSelectedFunc: (selected: ClientReportInfo | null) => void;
}

export function ClientProfileReportTableLite({
                                                 reports,
                                                 selected,
                                                 hideModalFunc,
                                                 loading,
                                                 setSelectedFunc,
                                             }: ClientProfileReportTableLiteProps) {
    return <ClientProfileReportTable reports={reports}
                                     selected={selected}
                                     setSelectedFunc={setSelectedFunc}
                                     hideModalFunc={hideModalFunc}
                                     isLite={true}
                                     loading={loading}
                                     columns={ClientReportColumnsLite as ClientReportColumn[]}
                                     isApplyGroupBySfOperationCase={true}/>
}

type ClientProfileReportTableProps = {
    reports: Array<ClientReportInfo>;
    selected: ClientReportInfo | null;
    isLite?: boolean;
    columns: ClientReportColumn[];
    loading: boolean;
    hideModalFunc: () => void;
    sortBy?: ClientReportSortBy
    setSortBy?: (selected: Paths<ClientReportInfo>) => void;
    setSelectedFunc: (selected: ClientReportInfo | null) => void;
    isApplyGroupBySfOperationCase?: boolean;
}

function ClientProfileReportTable({
                                      reports,
                                      selected,
                                      hideModalFunc,
                                      setSelectedFunc,
                                      columns,
                                      isLite = false,
                                      sortBy,
                                      setSortBy,
                                      loading,
                                      isApplyGroupBySfOperationCase,
                                  }: ClientProfileReportTableProps) {

    return (
        <div className={`w-full overflow-y-scroll block scrollbar-always ${isLite ? "text-sm" : "flex-1 min-h-0"}`}
             style={isLite ? {height: "calc(100vh - 68px - 100px - 37px - 90px - 40px)"} : undefined}>
            <table className='table table-auto w-full'>
                <thead>
                <tr className='text-left select-none text-sm text-gray-portal-table-title'>
                    {
                        columns.map((column) => (
                            <th
                                key={column.attr}
                                className={'font-light' + (column.attr === 'clientReportFiles' ? ' pl-4' : '')}
                                onClick={() => setSortBy && setSortBy(column.attr)}
                            >
                                <div className='flex items-center'>
                                    <div>
                                        {column.name}
                                    </div>
                                    {
                                        sortBy &&
                                        <div className='w-6 h-6 pl-2'>
                                            {sortBy.column === column.attr && sortBy.order === SortOrder.ASC &&
                                                <Icon className='align-middle' icon={CaretUpIcon}/>}
                                            {sortBy.column === column.attr && sortBy.order === SortOrder.DESC &&
                                                <Icon className='align-middle' icon={CaretDownIcon}/>}
                                        </div>
                                    }
                                </div>
                            </th>
                        ))
                    }
                </tr>
                </thead>
                <tbody>
                {
                    !loading && reports.map((report) =>
                        <ClientReportRowItem key={report.guid}
                                             report={report}
                                             setSelectedFunc={setSelectedFunc}
                                             hideModalFunc={hideModalFunc}
                                             columns={columns}
                                             selected={selected?.guid === report.guid}
                                             isApplyGroupBySfOperationCase={isApplyGroupBySfOperationCase}
                        />
                    )
                }
                </tbody>
            </table>
            {
                loading && <LoadingSpinner spacing={20} height={"40vh"}/>
            }
            {
                reports.length === 0 && !loading && <div className="w-full h-full flex flex-grow items-center">
                    <div className="m-auto">
                        No reports available.
                    </div>
                </div>
            }
        </div>
    );
}
